import { useMemo, useState } from 'react';
import { sessionSlots } from '../slots';

// Self-hosted forms gateway (matou_forms role in matou-distributed-servers):
// revalidates the slot server-side and emails the confirmation (cc the
// team) with a calendar invite via Stalwart.
const BOOKING_ENDPOINT = 'https://forms.matou.nz/api/booking/matou';
const MEETING_URL = 'https://meet.jit.si/matou-whanaungatanga';

const NZ = 'Pacific/Auckland';
const dayLabel = (d: Date) =>
  new Intl.DateTimeFormat('en-NZ', { timeZone: NZ, weekday: 'long', day: 'numeric', month: 'long' }).format(d);
const nzTime = (d: Date) =>
  new Intl.DateTimeFormat('en-NZ', { timeZone: NZ, hour: 'numeric', minute: '2-digit', hour12: true }).format(d);
const localTime = (d: Date) =>
  new Intl.DateTimeFormat(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }).format(d);

const inputCls =
  'w-full px-4 py-3 border border-black/10 rounded-md font-mono text-sm text-matou-text bg-matou-bg focus:outline-none focus:ring-2 focus:ring-matou-teal-medium focus:border-matou-teal-medium transition-colors';
const labelCls =
  'block font-mono text-xs font-semibold text-matou-text uppercase tracking-wider mb-2';
const linkCls = 'text-matou-teal-medium hover:underline';

export default function BookingForm() {
  // Slots anchor to the day the visitor is looking at the page. Kept as
  // state (not a one-shot memo) so a stale slot list can be regenerated —
  // e.g. after a 400 in a days-old tab where the original slots expired.
  const [slots, setSlots] = useState(() => sessionSlots());
  const inNz = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone === NZ, []);
  const days = useMemo(() => {
    const grouped = new Map<string, Date[]>();
    for (const s of slots) {
      const key = dayLabel(s);
      grouped.set(key, [...(grouped.get(key) ?? []), s]);
    }
    return [...grouped.entries()];
  }, [slots]);

  const [selected, setSelected] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState(''); // honeypot
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'slot-gone' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected) return;
    setStatus('submitting');
    try {
      const response = await fetch(BOOKING_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          slot: selected,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          website,
        }),
      });
      if (response.ok) {
        setStatus('success');
      } else if (response.status === 400) {
        setStatus('slot-gone');
        setSlots(sessionSlots());
        setSelected('');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  if (status === 'success') {
    const booked = new Date(selected);
    return (
      <div className="bg-matou-surface rounded-lg border border-black/10 p-6 md:p-8 space-y-4">
        <h2 className="font-heading text-xl font-bold text-matou-text">
          Ka pai — your session is booked
        </h2>
        <p className="font-mono text-sm leading-relaxed">
          {dayLabel(booked)} at {nzTime(booked)} NZT
          {!inNz && <> ({localTime(booked)} your time)</>}. A confirmation
          email with a calendar invite is on its way to you.
        </p>
        <p className="font-mono text-sm leading-relaxed">
          Join on the day via{' '}
          <a href={MEETING_URL} className={linkCls}>{MEETING_URL}</a>
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Honeypot: hidden from real users; the gateway silently drops any
          submission where this field is filled in */}
      <div className="absolute -left-[9999px]" aria-hidden="true">
        <label htmlFor="booking-website">Website</label>
        <input type="text" id="booking-website" name="website" value={website} tabIndex={-1}
          autoComplete="off" onChange={(e) => setWebsite(e.target.value)} />
      </div>

      <div className="bg-matou-surface rounded-lg border border-black/10 p-6 md:p-8 space-y-6">
        <h2 className="font-heading text-lg font-bold text-matou-text">Pick a session</h2>
        {days.map(([label, daySlots]) => (
          <fieldset key={label}>
            <legend className={labelCls}>{label} (NZT)</legend>
            <div className="space-y-2">
              {daySlots.map((s) => {
                const iso = s.toISOString();
                return (
                  <label key={iso} className="flex items-start gap-3 font-mono text-sm cursor-pointer">
                    <input type="radio" name="slot" checked={selected === iso} required
                      onChange={() => setSelected(iso)} className="mt-0.5 accent-matou-teal-medium" />
                    <span>
                      {nzTime(s)} NZT
                      {!inNz && (
                        <span className="text-matou-text-muted"> — {localTime(s)} your time</span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>
        ))}
      </div>

      <div className="bg-matou-surface rounded-lg border border-black/10 p-6 md:p-8 space-y-5">
        <h2 className="font-heading text-lg font-bold text-matou-text">Your details</h2>
        <div>
          <label htmlFor="booking-name" className={labelCls}>Ko wai koe? / Full Name *</label>
          <input id="booking-name" type="text" value={name} required
            onChange={(e) => setName(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label htmlFor="booking-email" className={labelCls}>He aha tō īmēra? / Email *</label>
          <input id="booking-email" type="email" value={email} required
            onChange={(e) => setEmail(e.target.value)} className={inputCls} />
        </div>
      </div>

      {status === 'slot-gone' && (
        <div className="bg-accessibility-fill border border-accessibility-stroke/30 rounded-md p-4" role="alert">
          <p className="font-mono text-sm text-accessibility-stroke">
            That time is no longer available — please pick another session and
            try again.
          </p>
        </div>
      )}
      {status === 'error' && (
        <div className="bg-accessibility-fill border border-accessibility-stroke/30 rounded-md p-4" role="alert">
          <p className="font-mono text-sm text-accessibility-stroke">
            Sorry, something went wrong. Please try again, or email us directly
            at contact@matou.nz.
          </p>
        </div>
      )}

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="w-full font-mono text-sm font-semibold bg-matou-teal-medium text-white px-6 py-3 rounded-md hover:bg-matou-teal-deep disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-150"
      >
        {status === 'submitting' ? 'Booking…' : 'Book this session'}
      </button>
    </form>
  );
}
